<template lang="pug">
	.flex-col.space-large.padding.full-height
		form(@submit="generateAiScript")
			DxForm(
				:form-data="localFormData",
				label-location="top",
				:col-count="1",
				width="100%"
			)
				DxGroupItem(:col-count="5")
					DxItem(
						data-field="product",
						:col-span="5",
						:validation-rules="validationRules.product",
						:editor-options='{placeholder: $tCaption("The name of your product/brand/category")}'
					)
					DxItem(
						data-field="category",
						:col-span="5",
						editor-type="dxTextArea",
						:validation-rules="validationRules.category",
						:editor-options='{placeholder: $tCaption("The description of your product/brand/category")}'
					)
					DxItem(
						data-field="length",
						editor-type="dxSelectBox",
						:editor-options='{placeholder: $tCaption("Choose the length"), items: lengthTypes}'
					)
					DxItem(
						css-class="conversation--form-item",
						data-field="conversation",
						editor-type="dxSwitch", :label='{location: "left",}'
					)
					DxItem(
						data-field="action",
						:validation-rules="validationRules.action",
						editor-type="dxSelectBox",
						:editor-options='{placeholder: $tCaption("Choose the action"), items: actionTypes, showClearButton: true}'
					)
					DxItem(
						v-if="localFormData.action",
						data-field="actionValue",
						:col-span="2",
						:validation-rules="validationRules.actionValue",
						:editor-options='{placeholder: `${$tCaption("Type the")} ${localFormData.action}`, }'
					)
						DxLabel(:text="localFormData.action")
				DxGroupItem(:col-count="5")
					DxItem(:col-span="5")
						template(v-slot:default="{ data }")
							.flex-row.space.align-center.justify-end
								DxButton(:text="$tCaption('Reset')", type="normal", @click="resetLocalForm")
								DxButton(:text="$tCaption('Generate')", type="default", :useSubmitBehavior="true")
		DxScrollView(ref="variantsView", width="100%" :height="`${variantsHeight}px`")
			.content
				DxList(
					:data-source="localDataSource"
					width="100%"
					:no-data-text="$tCaption('No suggestions loaded') + '...'"
					selection-mode="single"
					:selectedItems="localSelectedItems"
					@update:selectedItems="onSuggestionSelected"
				)
					template(v-slot:item="{data: item, index}")
						.flex-row.justify
							.flex-col
								.left {{ item.text }}
							.flex-col.no-padding
								.flex-row.action-buttons
									DxButton(
										icon="fas fa-thumbs-up",
										type="default",
										stylingMode="text"
										:hint="$tCaption('Get more suggestions like this')"
										@click="onLikeSuggestion(item)"
										)
				.flex-col.full-height.full-width.center.align-center.backdrop.absolute(v-if="loading")
					Loader()
</template>

<script>
	import $ from 'jquery';
	import {
		DxForm,
		DxGroupItem,
		DxEmptyItem,
		DxSimpleItem,
		DxItem,
		DxLabel,
		DxButtonItem,
		DxButtonOptions
	} from 'devextreme-vue/form';
	import DxButton from 'devextreme-vue/button';
	import DxList from 'devextreme-vue/list';
	import { DxLoadPanel } from 'devextreme-vue/load-panel';
	import { DxPopup } from 'devextreme-vue/popup';
	import { DxScrollView } from 'devextreme-vue/scroll-view';
	import ArrayStore from 'devextreme/data/array_store';
	import Loader from 'app-core/Loader.vue';
	import resizeObserver from 'resize-observer';

	const defaultValues = () => ({
		product: null,
		category: null,
		length: 'short',
		action: null,
		actionValue: null,
		conversation: false,
	})
	const MAX_PARAPHRASE_LENGTH = 1000;

	export default {
		translationCategory: 'creative.AiScriptBuilder.newScriptStep',
		name: "newScriptStep",
		components: {
			DxForm, DxEmptyItem, DxSimpleItem, DxGroupItem, DxItem, DxButtonItem,
			DxButton, DxButtonOptions,
			DxLabel, DxList, DxLoadPanel, DxPopup, DxScrollView, Loader
		},
		props: {
			formData: { type: Object, required: false, default: defaultValues },
			selectedItems: { type: Array, required: false, default: () => ([]) },
			dataSource: { type: Object, required: false, default: () => ({}) },
		},
		computed: {
			lengthTypes() {
				return [
					this.$tCaption("short"),
					this.$tCaption("medium"),
					this.$tCaption("long"),
				]
			},
			actionTypes() {
				return [
					this.$tCaption("Website"),
					this.$tCaption("Address"),
					this.$tCaption("Phone"),
				]
			}
		},
		data() {
			return {
				isVisible: false,
				loading: false,
				localSelectedItems: [],
				localFormData: defaultValues(),
				localDataSource: {},
				validationRules: {
					product: [{ type: 'required', message: 'The name of your product/brand/category is required.' }],
					category: [{
						type: 'required',
						message: 'The description of your product/brand/category is required.'
					}],
					actionValue: [{ type: 'required', message: 'The action value is required.' }],
				},
				variantsHeight: 200,
				resetButtonOptions: {
					text: 'Reset',
					type: 'reset',
					[':on-click']: 'resetLocalForm'
				},
				generateButtonOptions: {
					text: 'Generate',
					type: 'default',
					useSubmitBehavior: true,
				},
				MAX_PARAPHRASE_LENGTH: 1000,
				isParaphrasing: false
			}
		},
		watch: {
			formData: {
				handler(v) {
					this.localFormData = v;
				},
				immediate: true,
			},
			selectedItems: {
				handler(v) {
					this.localSelectedItems = v;
				},
				immediate: true,
			},
			dataSource: {
				handler(v) {
					this.localDataSource = v;
				},
				immediate: true,
			},
		},
		mounted() {
			if (__DEBUG__)
				window.newScriptStep = this;
			this.$unobserve = resizeObserver
				.observe(this, this.$el, () => {
					if (__DEBUG__) console.debug('Observer');
					this.calcSizes();
				});
		},
		destroyed() {
			if (this.$unobserve)
				this.$unobserve();
		},
		methods: {
			getAiScriptData(url, payload) {
				const data = { ...payload, clientIp: this.$auth.userIP() }
				return this.$Data
					.postExternalApi(`OpenAiGpt3/${url}`, data)
					.then(r => {
						if (r.Success) return JSON.parse(r.Data.Content);
						else this.$notifyError(r.ErrorMessage);
					});
			},
			async requestSuggestions({ url, payload }) {
				this.loading = true;
				try {
					const data = await this.getAiScriptData(url, payload);
					const items = data.result.map((item, i) => ({ index: i, text: item }));
					this.localDataSource = new ArrayStore({
						key: "index",
						data: items,
					});
					if (this.isParaphrasing && items.length) {
						this.onSuggestionSelected([items[0]]);
					}
				} catch (err) {
					this.$notifyError(err);
				} finally {
					this.loading = false;
				}
			},
			generateAiScript(e) {
				e.preventDefault();
				this.localSelectedItems = [];
				this.isParaphrasing = false;
				this.requestSuggestions(this.setGenerateParams());
			},
			resetLocalForm() {
				this.localDataSource = {};
				this.localFormData = defaultValues();
			},

			onLikeSuggestion(suggestion) {
				this.localSelectedItems = [];
				this.isParaphrasing = true;
				this.requestSuggestions(this.setParaphraseParams(suggestion.text));
			},
			onSuggestionSelected(e) {
				this.localSelectedItems = e;
				this.$emit('data-changed', {
					stepName: 'newScript',
					canNext: this.localSelectedItems.length,
					data: {
						formData: this.localFormData,
						selectedItems: this.localSelectedItems,
						dataSource: this.localDataSource,
					}
				})
			},

			setGenerateParams() {
				const url = 'Generate';
				let { product, category, length, action, conversation, actionValue } = this.localFormData;
				let payload = { product, category, length, action, conversation, promptName: 'CREATIVESTUDIO' };

				if (payload.action) {
					payload.action = payload.action.toLowerCase();
					payload[payload.action] = actionValue;
				}
				return { url, payload };
			},
			setParaphraseParams(text) {
				const url = 'Paraphrase';
				const payload = { script: text.slice(0, MAX_PARAPHRASE_LENGTH), promptName: 'CREATIVESTUDIO' };
				return { url, payload };
			},

			calcSizes() {
				let variants = this.$refs.variantsView.$el;
				if (variants) {
					this.variantsHeight = Math.max(200, $(window).height() - $(variants).offset().top - 80);
				}
			},
		}
	}
</script>

<style lang="scss">
	.scroll-view {
		//flex: 1 1 auto;
		//overflow-y: auto;
		//min-height: 0;
	}

	.conversation--form-item {
		align-self: center;
	}

	.dx-field-item-label-location-top {
		padding: 0;
	}

	.dx-field-item-content .dx-switch {
		margin-top: 0 !important;
	}

	.dx-list-item-content {
		&:before {
			display: none;
		}

		white-space: normal;
	}

	.dx-list .dx-empty-message {
		text-align: center;
	}

	.dx-list .dx-item.dx-list-item {
		&:hover .action-buttons {
			opacity: 100;
			transition: opacity 0.2s ease-in;
		}

		.action-buttons {
			position: relative;
			top: 0;
			right: 0;
			transition: opacity 0.3s ease-out;
			opacity: 0;
		}
	}
</style>
